import React, { useState, useEffect, useRef } from 'react';
import confetti from 'canvas-confetti';
import { ImageBox } from './ImageBox';
import { BASE_ANIMATION_DURATIONS, RARITY_SPEED_MULTIPLIERS, RARITY_VISUAL_EFFECTS } from '../config/animationConfig';
import './UpdateModal.css';

interface UpdateModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// NIFC animation timing: 1250ms / 0.7 = ~1786ms dramatic slow flip
const NIFC_FLIP_DURATION = Math.round(
  BASE_ANIMATION_DURATIONS.enterAndFlipMs / (RARITY_SPEED_MULTIPLIERS.NIFC ?? 0.7)
);

export const UpdateModal: React.FC<UpdateModalProps> = ({ isOpen, onClose }) => {
  const [animKey, setAnimKey] = useState<number>(0);
  const [isAnimating, setIsAnimating] = useState<boolean>(true);
  const [isStatsRevealed, setIsStatsRevealed] = useState<boolean>(false);
  const [isFlashing, setIsFlashing] = useState<boolean>(false);
  const flashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      setIsStatsRevealed(false);
      setIsFlashing(false);
      setAnimKey((prev) => prev + 1);
    }
    return () => {
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleFlipComplete = () => {
    setIsAnimating(false);
    setIsStatsRevealed(true);
    setIsFlashing(true);

    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    flashTimerRef.current = setTimeout(() => {
      setIsFlashing(false);
    }, 550);

    try {
      const nifcEffects = RARITY_VISUAL_EFFECTS.NIFC;
      confetti({
        particleCount: nifcEffects?.particleCount || 55,
        spread: 75,
        origin: { y: 0.45 },
        colors: nifcEffects?.confettiColors || ['#d97706', '#fbbf24', '#fde68a', '#ffffff', '#b45309'],
        disableForReducedMotion: true,
      });
    } catch {
      // Fallback if confetti canvas is unavailable
    }
  };

  return (
    <>
      {/* Screen flash effect matching real hunting */}
      {isFlashing && (
        <div
          className="screen-flash-overlay"
          style={{ background: RARITY_VISUAL_EFFECTS.NIFC?.flashColor }}
        />
      )}

      <div className="modal-backdrop" onClick={onClose}>
        <div className="update-modal-content" onClick={(e) => e.stopPropagation()}>
          {/* Top-right X button */}
          <button className="modal-close-btn" onClick={onClose} aria-label="Close modal">
            &times;
          </button>

          {/* Header */}
          <h2 className="update-modal-header-title">New Update</h2>

          {/* Stage Row: Left Image Box + Right Stats */}
          <div className="update-stage-row">
            <div className="update-image-wrapper">
              <ImageBox
                key={animKey}
                imagePath="mc_commemorative_2007_2euro_0.webp"
                denomination="2euro"
                year={2007}
                coinId={`update-nifc-${animKey}`}
                isAnimating={isAnimating}
                enterDurationMs={NIFC_FLIP_DURATION}
                shouldFlip={true}
                onFlipComplete={handleFlipComplete}
              />
            </div>

            <div className={`update-stats-box ${isStatsRevealed ? 'stats-revealed' : 'stats-hidden'}`}>
              <div className="update-stats-title">
                Monaco 2€ CC (2007) - Grace Kelly
              </div>
              <table className="update-stats-table">
                <tbody>
                  <tr className="update-mintage-row">
                    <td>Mintage:</td>
                    <td className="update-mintage-val">20,001</td>
                  </tr>
                  <tr>
                    <td>Country:</td>
                    <td>Monaco</td>
                  </tr>
                  <tr>
                    <td>Year:</td>
                    <td>2007</td>
                  </tr>
                  <tr>
                    <td>Strike:</td>
                    <td>In Sets / Collector</td>
                  </tr>
                  <tr>
                    <td>Status:</td>
                    <td className="update-status-nifc">NIFC</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Bottom Announcement */}
          <ul className="update-modal-text">
            <li><b>NIFC coins</b> can be found in rolls!</li>
            <li><b>Achievements</b> are out!</li>
            <li>Circulation coins with <b>unknown mintage</b> can be found!</li>
              (e.g. Bulgaria 2026, Netherlands 2026)
          </ul>

          {/* Action Button */}
          <button className="update-modal-btn" onClick={onClose}>
            Awesome
          </button>
        </div>
      </div>
    </>
  );
};
